import { useEffect, useRef } from 'react';
import { cn } from '../lib/cn';

/**
 * Showcase background for the login screen: slow "ink in water"
 * clouds of the accent color over paper, with film grain. One
 * fragment shader on a low-resolution canvas, capped at 30 fps,
 * paused while the tab is hidden. Reduced motion draws one still
 * frame; without WebGL the CSS gradient on the wrapper stays.
 */

const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';

const FRAG = `precision mediump float;
uniform vec2 r;uniform float t;uniform vec3 paper;uniform vec3 ink;uniform vec3 glow;
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<4;i++){v+=a*n(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}
void main(){
  vec2 uv=gl_FragCoord.xy/r;vec2 p=uv*vec2(r.x/r.y,1.)*1.6;float s=t*.035;
  vec2 q=vec2(fbm(p+s),fbm(p+vec2(5.2,1.3)-s));
  vec2 w=vec2(fbm(p+3.*q+vec2(1.7,9.2)+s*1.3),fbm(p+3.*q+vec2(8.3,2.8)-s));
  float f=fbm(p+3.5*w);
  float m=smoothstep(.3,.8,f)*(.45+.55*uv.y);
  vec3 c=mix(paper,ink,m*.42);
  c=mix(c,glow,smoothstep(.6,1.1,length(w-q))*.5);
  c+=(h(gl_FragCoord.xy+fract(t))-.5)*.035;
  gl_FragColor=vec4(c,1.);
}`;

/** Resolve any CSS color (incl. oklch) to linear-ish 0..1 RGB via 2D canvas. */
function cssRgb(ctx: CanvasRenderingContext2D, color: string): [number, number, number] {
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const d = ctx.getImageData(0, 0, 1, 1).data;
  return [d[0] / 255, d[1] / 255, d[2] / 255];
}

export function InkField({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const gl = canvas?.getContext('webgl', { antialias: false, premultipliedAlpha: false });
    if (!canvas || !gl) return;

    const prog = gl.createProgram();
    if (!prog) return;
    for (const [type, src] of [
      [gl.VERTEX_SHADER, VERT],
      [gl.FRAGMENT_SHADER, FRAG],
    ] as const) {
      const sh = gl.createShader(type);
      if (!sh) return;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      gl.attachShader(prog, sh);
    }
    gl.bindAttribLocation(prog, 0, 'p');
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    const u = (name: string) => gl.getUniformLocation(prog, name);
    const [uR, uT, uPaper, uInk, uGlow] = ['r', 't', 'paper', 'ink', 'glow'].map(u);

    const probe = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    const readColors = (): void => {
      if (!probe) return;
      const css = getComputedStyle(document.documentElement);
      const v = (name: string) => `oklch(${css.getPropertyValue(name).trim()})`;
      gl.uniform3fv(uPaper, cssRgb(probe, v('--sidebar')));
      gl.uniform3fv(uInk, cssRgb(probe, v('--brand')));
      gl.uniform3fv(uGlow, cssRgb(probe, v('--bubble')));
    };

    const resize = (): void => {
      // ~0.4x resolution: the field is soft, so pixels are wasted
      // work, and phone GPUs stay cool.
      const scale = Math.min(window.devicePixelRatio || 1, 2) * 0.4;
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * scale));
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * scale));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uR, canvas.width, canvas.height);
    };

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Random phase so every visit shows a different composition.
    const t0 = performance.now() - Math.random() * 600_000;
    let raf = 0;
    let last = 0;
    const draw = (now: number): void => {
      gl.uniform1f(uT, (now - t0) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    const loop = (now: number): void => {
      raf = requestAnimationFrame(loop);
      if (now - last < 33) return; // ~30 fps is plenty for a slow drift
      last = now;
      draw(now);
    };
    const start = (): void => {
      cancelAnimationFrame(raf);
      if (reduce.matches || document.hidden) draw(performance.now());
      else raf = requestAnimationFrame(loop);
      canvas.style.opacity = '1';
    };

    readColors();
    resize();
    start();
    const ro = new ResizeObserver(() => {
      resize();
      draw(performance.now());
    });
    ro.observe(canvas);
    const theme = new MutationObserver(() => {
      readColors();
      draw(performance.now());
    });
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', start);
    reduce.addEventListener('change', start);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      theme.disconnect();
      document.removeEventListener('visibilitychange', start);
      reduce.removeEventListener('change', start);
      // No loseContext(): StrictMode re-runs this effect on the same
      // canvas, and a lost context can't be reacquired. The browser
      // frees it with the canvas.
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none overflow-hidden bg-[radial-gradient(60%_50%_at_20%_15%,oklch(var(--brand)/0.18),transparent_70%),radial-gradient(50%_45%_at_85%_80%,oklch(var(--brand)/0.12),transparent_70%)]',
        className,
      )}
    >
      <canvas ref={ref} className="h-full w-full opacity-0 transition-opacity duration-1000" />
    </div>
  );
}
